import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../api';

const MULTIPLE_UPLOAD_DOCS = [
  'Experience letters',
  'Degree certificates',
  'Passport',
  'Pay Slips',
  'Bank Statement'
];

const DocumentUploadPage = () => {
  const { token } = useParams();

  const [stage, setStage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [maskedEmail, setMaskedEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [otp, setOtp] = useState('');

  const [empDetails, setEmpDetails] = useState({});
  const [requestedDocs, setRequestedDocs] = useState([]);
  const [alreadyUploaded, setAlreadyUploaded] = useState([]);

  const [filesToUpload, setFilesToUpload] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const otpTriggered = useRef(false);

  useEffect(() => {
    if (!otpTriggered.current) {
      otpTriggered.current = true;
      triggerOtp();
    }
    // eslint-disable-next-line
  }, [token]);

  const triggerOtp = async () => {
    try {
      const res = await api.post(`/document-requests/public/trigger-otp/${token}`);
      setMaskedEmail(res.data.masked_email);
      setFirstName(res.data.employee_name);
      setStage(1);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'This link is invalid or has expired.');
      setStage(4);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      alert('Please enter a valid 6-digit OTP.');
      return;
    }

    setLoading(true);

    try {
      const res = await api.post(`/document-requests/public/verify-otp/${token}`, { otp });

      setEmpDetails(res.data.employee_details || {});
      setRequestedDocs(res.data.requested_docs || []);
      setAlreadyUploaded(res.data.already_uploaded || []);
      setStage(2);
    } catch (err) {
      let msg = 'Invalid OTP.';
      const detail = err.response?.data?.detail;

      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d) => d.msg).join(' | ');
      }

      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (docType, e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (selectedFiles.length === 0) return;

    setFilesToUpload((prev) => {
      const isMultiple = MULTIPLE_UPLOAD_DOCS.includes(docType);
      const existing = prev[docType] || [];

      return {
        ...prev,
        [docType]: isMultiple ? [...existing, ...selectedFiles] : [selectedFiles[0]]
      };
    });

    e.target.value = '';
  };

  const removeFile = (docType, index) => {
    setFilesToUpload((prev) => {
      const existingFiles = prev[docType] || [];
      const updated = [...existingFiles];

      updated.splice(index, 1);

      return {
        ...prev,
        [docType]: updated
      };
    });
  };

  const isReadyToSubmit = requestedDocs.every(
    (doc) => alreadyUploaded.includes(doc) || (filesToUpload[doc] && filesToUpload[doc].length > 0)
  );

  const handleSubmitAll = async () => {
    if (!isReadyToSubmit) return;

    setSubmitLoading(true);

    try {
      for (const docType of Object.keys(filesToUpload)) {
        for (const file of filesToUpload[docType]) {
          const fd = new FormData();

          fd.append('document_type', docType);
          fd.append('file', file);

          await api.post(`/document-requests/public/upload/${token}`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      await api.post(`/document-requests/public/submit/${token}`);
      setStage(3);
    } catch (err) {
      alert(err.response?.data?.detail || 'An error occurred during submission. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading && stage === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        py: { xs: 2, md: 4 },
        px: { xs: 1.5, sm: 2 }
      }}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          <img
            src="/logo_main.png"
            alt="Transact Global"
            style={{
              height: 90,
              objectFit: 'contain',
              maxWidth: '100%'
            }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)',
            bgcolor: '#ffffff'
          }}
        >
          {stage === 1 && (
            <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#0c4da8',
                  mb: 1
                }}
              >
                Welcome to Transact Global
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 4,
                  lineHeight: 1.7
                }}
              >
                Hi <b>{firstName}</b>,
                <br />
                For security purposes, we have sent a 6-digit OTP to your email:
                <br />
                <b>{maskedEmail}</b>
                <br />
                Please enter it below to proceed with your document upload.
              </Typography>

              <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                <TextField
                  fullWidth
                  label="Enter 6-Digit OTP"
                  variant="outlined"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  sx={{ mb: 3 }}
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      letterSpacing: '0.45rem',
                      fontSize: '1.1rem',
                      fontWeight: 700
                    }
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={verifyOtp}
                  disabled={loading || otp.length !== 6}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 20,
                    py: 1,
                    fontWeight: 700,
                    bgcolor: '#0c4da8',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#083b7a',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify & Proceed'}
                </Button>
              </Box>
            </Box>
          )}

          {stage === 2 && (
            <Box>
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff',
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#0c4da8',
                    mb: 0.5,
                    fontSize: { xs: '1.05rem', md: '1.2rem' }
                  }}
                >
                  Employee Document Upload
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    mb: 2.5,
                    fontSize: { xs: '0.82rem', md: '0.9rem' }
                  }}
                >
                  Please upload the requested documents securely for HR verification.
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {empDetails.name}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Designation
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {empDetails.designation}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Email
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#111827',
                          wordBreak: 'break-word'
                        }}
                      >
                        {empDetails.email}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Date of Joining
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {empDetails.doj}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', md: '0.95rem' }
                  }}
                >
                  Requested Documents
                </Typography>

                {requestedDocs.map((doc, idx) => {
                  const isUploaded = alreadyUploaded.includes(doc);
                  const isMultiple = MULTIPLE_UPLOAD_DOCS.includes(doc);
                  const queuedFiles = filesToUpload[doc] || [];
                  const isSatisfied = isUploaded || queuedFiles.length > 0;

                  return (
                    <Card
                      key={idx}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        borderColor: isSatisfied ? '#16a34a' : '#e5e7eb',
                        bgcolor: isSatisfied ? '#f0fdf4' : '#ffffff',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)'
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 1.75, md: 2 },
                          '&:last-child': {
                            pb: { xs: 1.75, md: 2 }
                          },
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'stretch', sm: 'center' },
                          justifyContent: 'space-between',
                          gap: 1.5
                        }}
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: '#111827',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              flexWrap: 'wrap'
                            }}
                          >
                            {doc}
                            {isSatisfied && (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 17,
                                  color: '#16a34a'
                                }}
                              />
                            )}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              display: 'block',
                              mt: 0.4
                            }}
                          >
                            {isMultiple
                              ? 'Multiple files allowed for this document.'
                              : 'Accepted formats: PDF, JPG, PNG.'}
                          </Typography>

                          {queuedFiles.length > 0 && (
                            <Box
                              sx={{
                                mt: 1.25,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75
                              }}
                            >
                              {queuedFiles.map((file, fIdx) => (
                                <Chip
                                  key={fIdx}
                                  label={file.name}
                                  onDelete={() => removeFile(doc, fIdx)}
                                  deleteIcon={<DeleteIcon />}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    maxWidth: '100%',
                                    bgcolor: '#ffffff',
                                    borderColor: '#cbd5e1',
                                    '& .MuiChip-label': {
                                      maxWidth: 180,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                          <input
                            accept="image/*,.pdf"
                            style={{ display: 'none' }}
                            id={`upload-${idx}`}
                            type="file"
                            multiple={isMultiple}
                            onChange={(e) => handleFileChange(doc, e)}
                          />

                          <label htmlFor={`upload-${idx}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<CloudUploadIcon />}
                              size="small"
                              sx={{
                                textTransform: 'none',
                                borderRadius: 20,
                                px: 2,
                                py: 0.65,
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                borderColor: '#0c4da8',
                                color: '#0c4da8',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: '#083b7a',
                                  bgcolor: '#eff6ff'
                                }
                              }}
                            >
                              {isUploaded ? 'Replace' : isMultiple ? 'Select Files' : 'Select File'}
                            </Button>
                          </label>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                <Divider sx={{ my: 3 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5
                  }}
                >
                  {!isReadyToSubmit ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                      Attach all requested documents before submitting.
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                      All documents are ready to submit.
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    disabled={!isReadyToSubmit || submitLoading}
                    onClick={handleSubmitAll}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 20,
                      px: 3,
                      py: 0.85,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      bgcolor: '#0c4da8',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: '#083b7a',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {submitLoading ? <CircularProgress size={18} color="inherit" /> : 'Submit Documents'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {stage === 3 && (
            <Box sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 72, color: '#16a34a', mb: 2 }} />

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: '#111827'
                }}
              >
                Submission Successful
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.7,
                  maxWidth: 520,
                  mx: 'auto'
                }}
              >
                Thank you for completing your documentation process. Your files have been securely sent
                to the HR team for review. This link is now deactivated. You may close this window.
              </Typography>
            </Box>
          )}

          {stage === 4 && (
            <Box sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
              <ErrorOutlineIcon sx={{ fontSize: 72, color: '#dc2626', mb: 2 }} />

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: '#dc2626'
                }}
              >
                Link Expired or Invalid
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.7,
                  maxWidth: 520,
                  mx: 'auto'
                }}
              >
                {errorMsg}
                <br />
                <br />
                Please contact the Transact Global HR team to generate a new document upload link.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default DocumentUploadPage;